// The code up here diverges from the template, as the imports are from the package
// and not from the relative template directories.
import _ from 'lodash'
import React from 'react'
import { BrowserRouter } from 'react-router-dom'

import '@paytrace/pt-skel/base.css'
import './App.css'

import { Loader } from '@paytrace/pt-skel/Component'
import { getEpoch } from '@paytrace/pt-skel/Helper/DateTime'
import { TokenInspector } from '@paytrace/pt-skel/Helper/TokenInspector'
import { getFromQueryString } from '@paytrace/pt-skel/Helper/URL'

import PubSub from '@paytrace/pt-skel/Event/PubSub'

import { AppCtx, AppCtxI, defaultAppCtx } from '@paytrace/pt-skel/Context'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import { useMountUnmount, useObjState } from '@paytrace/pt-skel/Hooks'

import * as Network from './Event/Network'
import Event from './Event'
import { updateStitchToken } from '@paytrace/pt-skel/Helper/Stitch'
import Routes from './Routes'

// The code beyond this point should keep the same as the template
// in the skel/ directory, but if you're using this already forked, feel
// free to customize to your liking.
interface Props { }

// Actual values for many Ctx related things end up set in DynamicPage nesting
interface State {
  accessToken: string | null | undefined | { [key: string]: string }
  refreshToken: string | null | undefined
  authProvider: string
  debugMode: boolean
  hasNetworkBound: boolean
  merchant: string
  merchantLabel: string
  username: string | null
  usernameLabel: string | null
}

// Following this thread - parcel HMR (even the full dom refresh)
// has many issues that occur with custom event handlers/bindings.
// As we are likely to use those, and this is impacting the PubSub bindings
// by causing duplicate subs, we can use the last comment in this thread
// as the solution that seems to work to trigger true page refreshes on changes.
// I don't believe this will be too cumbersome for workflows of front end,
// as parcel didn't use the incremental HMR by default anyways (so form input values
// would be lost regardless).
// https://github.com/parcel-bundler/parcel/issues/289
const hot = module && (module as any)?.hot
if (hot) {
  hot.dispose(() => {
    window.location.reload()
    throw new Error('hotReload')
  })
}

function getAccessToken (): string | null {
  return Persistence.getItem('access-token')
}

function getRefreshToken (): string | null {
  return Persistence.getItem('refresh-token')
}

function getUsernameFromToken (accessToken: string | null | undefined | { [key: string]: string }): string | null {
  if (null === accessToken || undefined === accessToken) return null

  const inspector = TokenInspector.fromJwt(accessToken)
  const payload = inspector.getParsed()

  return payload.username || null
}

function getDebugMode (): boolean {
  return 'y' === process.env.DEBUG_MODE
}

// TODO: Probably toss this and some other settings like access_token into
// a context builder that is used here, vs inline in App
function getAuthProvider (): string {
  if ('y' === process.env.DEBUG_MODE) {
    const queryString = getFromQueryString('auth-provider')

    if (queryString) {
      Persistence.setItem('auth-provider', queryString)
    }

    const priorSetting = Persistence.getItem('auth-provider')

    if (priorSetting) return priorSetting
  }

  return process.env.AUTH_PROVIDER || 'self'
}

const self = Symbol('App')

function renderIfLoaded (state: State) {
  if (state.hasNetworkBound) {
    return (Routes)
  }

  return (
    <main className='main-wrapper'>
      <section className='dyn-page'>
        <Loader style={{ marginTop: 100 }} />
      </section>
    </main>
  )
}

export function App (props: Props) {
  const [state, setState] = useObjState<State>({
    accessToken: getAccessToken(),
    refreshToken: getRefreshToken(),
    authProvider: getAuthProvider(),
    debugMode: getDebugMode(),
    merchant: '12345',
    merchantLabel: 'fake',
    username: getUsernameFromToken(getAccessToken()),
    usernameLabel: 'fake',
    hasNetworkBound: false,
  })

  useMountUnmount(() => {
    Network.init(self)

    PubSub.sub(
      self,
      Event.SET_ACCESS_TOKEN,
      (accessToken: string | { [key: string]: string }) => {
        // Should we add a timing short-circuit here, to ignore if multiples come in at once?
        if (!accessToken) {
          return
        }

        if (typeof accessToken === 'string') {
          console.debug('Setting access token to: ' + accessToken.slice(0, 10) + '...')
        } else {
          console.debug('Setting access token to: ', accessToken)
        }
        setState({
          accessToken,
          username: getUsernameFromToken(accessToken),
        })
        Persistence.setItem('access-token', accessToken)
      }
    )

    PubSub.sub(
      self,
      Event.SET_REFRESH_TOKEN,
      (refreshToken: string) => {
        // Should we add a timing short-circuit here, to ignore if multiples come in at once?
        if (!refreshToken) {
          return
        }

        console.debug('Setting refresh token to: ' + refreshToken.slice(0, 10) + '...')
        setState({
          refreshToken,
        })
        Persistence.setItem('refresh-token', refreshToken)
        updateStitchToken(refreshToken)
      }
    )

    PubSub.sub(
      self,
      Event.SET_USERNAME,
      (username: string) => {
        console.debug('Setting username to: ' + username)
        setState({ username })
        Persistence.setItem('username', username)
      }
    )

    // This is essentially a "mirror" listener for LOGIN_RESPONSE
    // If we do not listen here, for chaining the set calls, we do not
    // always catch the new AT/RT from refresh events or if login() was called
    // anywhere other than the base/LoginForm.
    // Unlike the LoginForm, we won't do any error handling here, leave that for
    // the form - just do the response spying here.
    PubSub.sub(
      self,
      Event.LOGIN_RESPONSE,
      (response: any) => {
        if ('success' !== _.get(response, 'status')) {
          return
        }

        // If it's a readable JWT, treat code as it had been
        if (response.data.access_token.slice(0, 3) === 'eyJ' &&
            response.data.access_token.includes('.')) {
          PubSub.pub(Event.SET_ACCESS_TOKEN, response.data.access_token)
        } else {
          // Otherwise, it's an encrypted one, we need to shuffle extra info in
          // This format matches how the values were read in TokenInspector
          PubSub.pub(Event.SET_ACCESS_TOKEN, {
            access_token: response.data.access_token,
            exp: getEpoch() + response.data.expires_in,
            username: response.data.username,
          })
        }
        PubSub.pub(Event.SET_REFRESH_TOKEN, response.data.refresh_token)
      }
    )

    // Without this to defer initial children renders, children
    // that want to call to the network publish to deaf-ears - the
    // Network.init() in here hasn't run yet - this staggers it, so an F5 reload
    // on a sub-page like /tranx.pay will still load CustomerSelect list
    // TODO: We may still need to fine tune to ensure the prior token/login is done.
    setState({ hasNetworkBound: true })

    return () => {
      Network.deinit(self)
      PubSub.unsub(self)
    }
  })

  const stateToContext = (): AppCtxI => {
    return {
      ...defaultAppCtx,
      accessToken: state.accessToken,
      authProvider: state.authProvider,
      debugMode: state.debugMode,
      merchant: state.merchant,
      merchantLabel: state.merchantLabel,
      username: state.username,
      usernameLabel: state.usernameLabel,
    }
  }

  const globalClick = (e: React.MouseEvent<HTMLElement>) => {
    PubSub.pub(Event.GLOBAL_CLICK, e.target)
  }

  return (
    <AppCtx.Provider value={stateToContext()}>
      <BrowserRouter>
        <div className='App' onClick={globalClick}>
            <span className='info'>
              PayTrace React Skeleton Slim - Copy this template and modify
              to create your own project.
            </span>
            {renderIfLoaded(state)}
        </div>
      </BrowserRouter>
    </AppCtx.Provider>
  )
}

export default App
